import { useState, useCallback, useRef, useEffect } from 'react';
import useGrainVarieties from '../../hooks/useGrainVarieties';
import GrainVarietyForm from '../../components/employee/GrainVarietyForm';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import { formatDate } from '../../utils/formatters';
import './EmployeeListPage.css';

const PAGE_SIZE = 20;

const truncate = (str, n) => str?.length > n ? str.slice(0, n) + '\u2026' : (str ?? '\u2014');

const EmployeeGrainVarieties = () => {
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingVariety, setEditingVariety] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // variety to delete
  const debounceRef = useRef(null);

  const {
    varieties,
    loading,
    isRefreshing,
    isSaving,
    isDeleting,
    error,
    pagination,
    fetchVarieties,
    fetchVariety,
    saveVariety,
    removeVariety,
    clearError,
  } = useGrainVarieties();

  const buildParams = useCallback(
    (overrides = {}) => ({
      search: searchTerm,
      page: pagination.page,
      ...overrides,
    }),
    [searchTerm, pagination.page]
  );

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchVarieties({ search: val, page: 1 });
    }, 300);
  };

  const handlePageChange = (page) => {
    fetchVarieties(buildParams({ page }));
  };

  const handleRowClick = async (row) => {
    try {
      const full = await fetchVariety(row.id);
      setEditingVariety(full);
      setView('form');
      setAlert(null);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load grain variety details.' });
    }
  };

  const handleAddNew = () => {
    setEditingVariety(null);
    setView('form');
    setAlert(null);
  };

  const handleCancel = () => {
    setEditingVariety(null);
    setView('list');
  };

  const handleSave = async (data) => {
    try {
      const id = editingVariety?.id || null;
      await saveVariety(data, id);
      setView('list');
      setEditingVariety(null);
      setAlert({
        type: 'success',
        message: id ? 'Grain variety updated.' : 'Grain variety created.',
      });
      fetchVarieties(buildParams({ page: 1 }));
    } catch (err) {
      setAlert({ type: 'error', message: err?.message || 'Failed to save grain variety.' });
    }
  };

  const handleDeleteClick = () => {
    if (editingVariety) setConfirmDelete(editingVariety);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await removeVariety(confirmDelete.id);
      setConfirmDelete(null);
      setView('list');
      setEditingVariety(null);
      setAlert({ type: 'success', message: `"${confirmDelete.name}" deleted.` });
      fetchVarieties(buildParams({ page: 1 }));
    } catch {
      setAlert({ type: 'error', message: 'Failed to delete grain variety.' });
      setConfirmDelete(null);
    }
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAlert({ type: 'error', message: error.message || 'An error occurred.' });
      clearError();
    }
  }, [error, clearError]);

  const columns = [
    { key: 'name', label: 'Name' },
    {
      key: 'description',
      label: 'Description',
      render: (val) => truncate(val, 80),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (val) => formatDate(val),
    },
  ];

  return (
    <div className="employee-page grain-varieties-page">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {view === 'list' ? (
        <>
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">Grain Varieties</h1>
          </div>

          <div className="emp-list__toolbar">
            <div className="emp-list__toolbar-left">
              <div className="emp-list__search-wrap">
                <svg className="emp-list__search-icon" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M8.5 3a5.5 5.5 0 014.383 8.823l3.896 3.9a.75.75 0 01-1.06 1.06l-3.9-3.896A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z" fill="currentColor" />
                </svg>
                <input
                  type="text"
                  className="emp-list__search"
                  placeholder="Search varieties..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label="Search grain varieties"
                />
              </div>
            </div>
            <Button variant="primary" size="small" onClick={handleAddNew}>
              + Add Variety
            </Button>
          </div>

          <div className="emp-list__body">
            {loading ? (
              <Loading text="Loading grain varieties..." />
            ) : (
              <>
                <Table
                  columns={columns}
                  data={varieties}
                  onRowClick={handleRowClick}
                  emptyMessage="No grain varieties found."
                  className="emp-list__table"
                />

                <div className="emp-list__cards">
                  {varieties.length === 0 ? (
                    <p className="emp-list__cards-empty">No grain varieties found.</p>
                  ) : (
                    varieties.map((variety) => (
                      <button
                        key={variety.id}
                        type="button"
                        className="emp-list__card"
                        onClick={() => handleRowClick(variety)}
                      >
                        <div className="emp-list__card-top">
                          <span className="emp-list__card-name">{variety.name}</span>
                        </div>
                        {variety.description && (
                          <div className="emp-list__card-subtitle">
                            {truncate(variety.description, 80)}
                          </div>
                        )}
                        <div className="emp-list__card-date">{formatDate(variety.created_at)}</div>
                        <svg className="emp-list__card-chevron" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M6 4L10 8L6 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ))
                  )}
                </div>

                <Pagination
                  currentPage={pagination.page}
                  totalCount={pagination.count}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                />
              </>
            )}
            {isRefreshing && <div className="emp-list__refreshing" />}
          </div>
        </>
      ) : (
        <>
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">
              {editingVariety ? `Edit: ${editingVariety.name}` : 'Add Grain Variety'}
            </h1>
          </div>

          <div className="emp-list__body emp-list__body--form">
            {loading ? (
              <Loading text="Loading..." />
            ) : (
              <>
                <GrainVarietyForm
                  variety={editingVariety}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                />
                {editingVariety && (
                  <div className="emp-list__danger-zone">
                    <div className="emp-list__danger-info">
                      <span className="emp-list__danger-label">Danger zone</span>
                      <span className="emp-list__danger-desc">
                        Permanently delete this grain variety. This cannot be undone.
                      </span>
                    </div>
                    <Button
                      variant="danger"
                      size="small"
                      onClick={handleDeleteClick}
                      disabled={isDeleting || isSaving}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Variety'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Confirm Delete"
        size="small"
      >
        <p className="emp-list__confirm-text">
          Are you sure you want to permanently delete <strong>{confirmDelete?.name}</strong>?
          This cannot be undone.
        </p>
        <div className="emp-list__confirm-actions">
          <Button variant="secondary" size="small" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="small"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeGrainVarieties;
